import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import "../CSS/ForcePasswordChangeModal.css";

const getMustChangePassword = (user) => Boolean(
  user?.password_must_change ||
  user?.must_change_password ||
  user?.requiere_cambio_password ||
  user?.debe_cambiar_password
);

const ForcePasswordChangeModal = () => {
  const { user, isAuthenticated, changePassword, logout } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const mustChangePassword = isAuthenticated && getMustChangePassword(user);

  const checks = useMemo(() => {
    const newPassword = formData.newPassword;
    const currentPassword = formData.currentPassword;
    const idCode = String(user?.codigo_institucional || "").trim();

    return {
      minLength: newPassword.length >= 8,
      hasLetter: /[A-Za-zÁÉÍÓÚÜÑáéíóúüñ]/.test(newPassword),
      hasNumber: /\d/.test(newPassword),
      differentFromCurrent: currentPassword && newPassword && currentPassword !== newPassword,
      differentFromId: !idCode || newPassword !== idCode,
      matches: newPassword && formData.confirmPassword && newPassword === formData.confirmPassword,
    };
  }, [formData, user]);

  const canSubmit = Object.values(checks).every(Boolean) && !submitting;

  if (!mustChangePassword) return null;

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) setError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    setError("");

    try {
      await changePassword({
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
      });

      setSuccess(true);
      window.setTimeout(() => {
        try {
          sessionStorage.setItem(
            "auth:loginMessage",
            "Contraseña actualizada correctamente. Inicia sesión nuevamente con tu nueva contraseña."
          );
        } catch { }
        logout();
        navigate("/login", { replace: true });
      }, 900);
    } catch (err) {
      setError(err?.message || "No se pudo actualizar la contraseña.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="force-password-overlay" role="presentation">
      <div
        className="force-password-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="forcePasswordTitle"
      >
        <div className="force-password-header">
          <div className="force-password-mark" aria-hidden="true">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="10" rx="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
          <div>
            <h2 id="forcePasswordTitle">Actualiza tu contraseña</h2>
            <p>Por seguridad, debes cambiar la contraseña inicial antes de continuar.</p>
          </div>
        </div>

        <form className="force-password-form" onSubmit={handleSubmit}>
          {error && <div className="force-password-alert error">{error}</div>}
          {success && (
            <div className="force-password-alert success">
              Contraseña actualizada. Cerraremos la sesión para que ingreses de nuevo.
            </div>
          )}

          <label className="force-password-field">
            <span>Contraseña actual</span>
            <input
              name="currentPassword"
              type={showPassword ? "text" : "password"}
              value={formData.currentPassword}
              onChange={handleChange}
              autoComplete="current-password"
              disabled={submitting || success}
            />
          </label>

          <label className="force-password-field">
            <span>Nueva contraseña</span>
            <input
              name="newPassword"
              type={showPassword ? "text" : "password"}
              value={formData.newPassword}
              onChange={handleChange}
              autoComplete="new-password"
              disabled={submitting || success}
            />
          </label>

          <label className="force-password-field">
            <span>Confirmar nueva contraseña</span>
            <input
              name="confirmPassword"
              type={showPassword ? "text" : "password"}
              value={formData.confirmPassword}
              onChange={handleChange}
              autoComplete="new-password"
              disabled={submitting || success}
            />
          </label>

          <button
            type="button"
            className="force-password-toggle"
            onClick={() => setShowPassword((prev) => !prev)}
            disabled={submitting || success}
          >
            {showPassword ? "Ocultar contraseñas" : "Mostrar contraseñas"}
          </button>

          <div className="force-password-checks" aria-live="polite">
            <PasswordCheck ok={checks.minLength}>Mínimo 8 caracteres</PasswordCheck>
            <PasswordCheck ok={checks.hasLetter}>Incluye letras</PasswordCheck>
            <PasswordCheck ok={checks.hasNumber}>Incluye números</PasswordCheck>
            <PasswordCheck ok={checks.differentFromCurrent}>Distinta a la contraseña actual</PasswordCheck>
            <PasswordCheck ok={checks.differentFromId}>No debe ser tu cédula</PasswordCheck>
            <PasswordCheck ok={checks.matches}>Las contraseñas coinciden</PasswordCheck>
          </div>

          <button
            type="submit"
            className="force-password-submit"
            disabled={!canSubmit || success}
          >
            {submitting ? "Actualizando..." : "Actualizar contraseña"}
          </button>
        </form>
      </div>
    </div>
  );
};

const PasswordCheck = ({ ok, children }) => (
  <div className={`force-password-check ${ok ? "ok" : ""}`}>
    <span aria-hidden="true">{ok ? "✓" : "•"}</span>
    <span>{children}</span>
  </div>
);

export default ForcePasswordChangeModal;
